import React, { FC, useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  useTheme,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SyncIcon from '@mui/icons-material/Sync'
import { BackButton } from '../../components/molecules/BackButton'
import {
  useJahisMasterPresetsQuery,
  useSendJahisMasterMutation,
} from '../../features/jahisMaster'
import { useToast } from '../../contexts/ToastContext'

export const JahisMasterPage: FC = () => {
  const [selectedPreset, setSelectedPreset] = useState<string>('')
  const theme = useTheme()
  const { showToast } = useToast()

  const { data: presetsData, isPending: isLoadingPresets } =
    useJahisMasterPresetsQuery()

  const sendMutation = useSendJahisMasterMutation()

  const handleSend = async () => {
    if (!selectedPreset) {
      showToast('プリセットを選択してください', 'error', 6000)
      return
    }

    sendMutation.mutate(
      { preset_name: selectedPreset },
      {
        onSuccess: (data) => {
          showToast(
            `用法マスタを送信しました（${data.rp_count}件のRP）`,
            'success',
            6000
          )
          setSelectedPreset('')
        },
        onError: () => {
          showToast('送信に失敗しました。再度お試しください。', 'error', 6000)
        },
      }
    )
  }

  return (
    <>
      {isLoadingPresets && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <Typography>読み込み中...</Typography>
        </Box>
      )}
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: theme.palette.grey[50],
          py: 4,
          px: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              gap: 2,
            }}
          >
            <SyncIcon
              sx={{
                fontSize: 48,
                color: theme.palette.primary.dark,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.dark,
                textAlign: 'center',
              }}
            >
              用法マスタ登録
            </Typography>
          </Box>

          <Paper sx={{ p: 4 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel id="preset-select-label">プリセット</InputLabel>
                <Select
                  labelId="preset-select-label"
                  value={selectedPreset}
                  label="プリセット"
                  onChange={(e) => setSelectedPreset(e.target.value)}
                  disabled={isLoadingPresets}
                >
                  {presetsData?.presets.map((preset) => (
                    <MenuItem key={preset} value={preset}>
                      用法マスタプリセット
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="large"
                startIcon={
                  sendMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                onClick={handleSend}
                disabled={!selectedPreset || sendMutation.isPending}
                sx={{
                  py: 1.5,
                  bgcolor: theme.palette.primary.dark,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    opacity: 0.9,
                  },
                }}
              >
                {sendMutation.isPending ? '送信中...' : '送信'}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <BackButton
        text="管理ポータルに戻る"
        position="fixed"
        variant="bottomLeft"
      />
    </>
  )
}
